import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import {
  LEFT,
  MEDIUM_VIEWPORT_MEDIA_QUERY,
  RIGHT,
  SCROLL_DISTANCE,
  TRANSLATION_KEY_CATEGORY_CAROUSEL,
} from "./constants";
import { useChildAccessibility, useMediaQuery, useScrollState } from "./hooks";
import styles from "./index.scss";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import {
  CarouselButton,
  ListElement,
  ScreenReaderInstructions,
} from "./components";

const CarouselWrapper = ({ showCarousel, children }) => {
  const { getTranslation } = useRuntimeConstants();
  return (
    <div
      className={classnames(styles.listContainer)}
      role={showCarousel ? "region" : undefined}
      aria-label={
        showCarousel
          ? getTranslation(TRANSLATION_KEY_CATEGORY_CAROUSEL)
          : undefined
      }
    >
      {children}
    </div>
  );
};

const HorizontalScrollList = ({ children }) => {
  const listRef = useRef(null);
  const isNotMobileViewport = useMediaQuery(MEDIUM_VIEWPORT_MEDIA_QUERY);
  const { hasOverflow, isAtStart, isAtEnd } = useScrollState(listRef, children);
  const { handleChildAccessibility } = useChildAccessibility(
    listRef,
    isNotMobileViewport
  );

  useEffect(() => {
    handleChildAccessibility();
  }, [handleChildAccessibility, hasOverflow]);

  const scroll = (direction) => {
    if (!listRef.current) return;

    listRef.current.scrollBy({
      left: direction === LEFT ? -SCROLL_DISTANCE : SCROLL_DISTANCE,
      behavior: "smooth",
    });
  };

  const showCarousel = hasOverflow && isNotMobileViewport;

  return (
    <CarouselWrapper showCarousel={showCarousel}>
      {showCarousel && (
        <>
          <ScreenReaderInstructions />
          <CarouselButton
            direction={LEFT}
            scroll={scroll}
            hasOverflow={hasOverflow}
            isAtScrollBoundary={isAtStart}
          />
        </>
      )}
      <ListElement listRef={listRef} showCarousel={showCarousel}>
        {children}
      </ListElement>
      {showCarousel && (
        <CarouselButton
          direction={RIGHT}
          scroll={scroll}
          hasOverflow={hasOverflow}
          isAtScrollBoundary={isAtEnd}
        />
      )}
    </CarouselWrapper>
  );
};

HorizontalScrollList.propTypes = {
  children: PropTypes.node.isRequired,
};

CarouselWrapper.propTypes = {
  showCarousel: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

export default HorizontalScrollList;
